/** Macro #30 — Creator social profile settings (handle + links). */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "../cn";
import { socialApi, SocialApiError, type ProfileLink } from "../../lib/social-api";
import { Spinner } from "./social-ui";
import { useI18n } from "../../i18n";

const MAX_LINKS = 6;

export function HandleSettings() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["social", "my-profile"],
    queryFn: ({ signal }) => socialApi.myProfile(signal),
    retry: false,
  });

  const [handle, setHandle] = useState("");
  const [links, setLinks] = useState<ProfileLink[]>([]);
  const [handleMsg, setHandleMsg] = useState<string | null>(null);
  const [linksMsg, setLinksMsg] = useState<string | null>(null);

  useEffect(() => {
    if (q.data) {
      setHandle(q.data.handle ?? "");
      setLinks(q.data.links ?? []);
    }
  }, [q.data]);

  const handleMut = useMutation({
    mutationFn: () => socialApi.setHandle(handle.trim()),
    onSuccess: () => { setHandleMsg(t("social.handle.handleSaved")); qc.invalidateQueries({ queryKey: ["social"] }); },
    onError: (e) => setHandleMsg(e instanceof SocialApiError ? e.message : t("social.handle.handleError")),
  });

  const linksMut = useMutation({
    mutationFn: () => socialApi.setLinks(links.filter((l) => l.url.trim().length > 0)),
    onSuccess: () => { setLinksMsg(t("social.handle.linksSaved")); qc.invalidateQueries({ queryKey: ["social"] }); },
    onError: (e) => setLinksMsg(e instanceof SocialApiError ? e.message : t("social.handle.linksError")),
  });

  const setLink = (i: number, patch: Partial<ProfileLink>) =>
    setLinks((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  return (
    <div data-testid="handle-settings" className="rounded-2xl border border-white/10 bg-[rgba(12,16,22,0.7)] p-5 space-y-5">
      <h3 className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/55">{t("social.handle.heading")}</h3>

      <div className="space-y-2">
        <label className="block font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">{t("social.handle.handleLabel")}</label>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[14px] text-white/40">@</span>
          <input
            data-testid="handle-input"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder={t("social.handle.handlePlaceholder")}
            className="flex-1 rounded-xl border border-white/12 bg-white/[0.03] px-3 py-2 text-[13px] text-white/90 placeholder:text-white/30 outline-none focus:border-neon-teal/60"
          />
          <button
            type="button"
            data-testid="handle-save"
            disabled={handleMut.isPending || handle.trim().length === 0}
            onClick={() => { setHandleMsg(null); handleMut.mutate(); }}
            className="inline-flex items-center gap-1.5 rounded-full border border-neon-teal/55 bg-neon-teal/10 px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-neon-teal hover:bg-neon-teal/20 disabled:opacity-40"
          >
            {handleMut.isPending && <Spinner />} {t("social.handle.save")}
          </button>
        </div>
        <p className="font-mono text-[10px] text-white/30">{t("social.handle.handleHint")}</p>
        {handleMsg && <p data-testid="handle-msg" className={cn("font-mono text-[10px]", handleMut.isError ? "text-state-danger" : "text-neon-teal")}>{handleMsg}</p>}
      </div>

      <div className="space-y-2">
        <label className="block font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">{t("social.handle.linksLabel")}</label>
        {links.map((l, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              data-testid={`link-label-${i}`}
              value={l.label}
              onChange={(e) => setLink(i, { label: e.target.value })}
              placeholder={t("social.handle.linkLabelPlaceholder")}
              className="w-28 rounded-lg border border-white/12 bg-white/[0.03] px-2.5 py-1.5 text-[12px] text-white/85 outline-none focus:border-neon-teal/60"
            />
            <input
              data-testid={`link-url-${i}`}
              value={l.url}
              onChange={(e) => setLink(i, { url: e.target.value })}
              placeholder="https://…"
              className="flex-1 rounded-lg border border-white/12 bg-white/[0.03] px-2.5 py-1.5 text-[12px] text-white/85 outline-none focus:border-neon-teal/60"
            />
            <button type="button" data-testid={`link-remove-${i}`} onClick={() => setLinks((p) => p.filter((_, idx) => idx !== i))}
                    className="text-white/40 hover:text-neon-coral text-[16px] leading-none px-1">×</button>
          </div>
        ))}
        {links.length < MAX_LINKS && (
          <button type="button" data-testid="link-add" onClick={() => setLinks((p) => [...p, { label: "", url: "" }])}
                  className="font-mono text-[11px] uppercase tracking-[0.12em] text-white/55 hover:text-white">{t("social.handle.addLink")}</button>
        )}
        <div className="pt-1">
          <button
            type="button"
            data-testid="links-save"
            disabled={linksMut.isPending}
            onClick={() => { setLinksMsg(null); linksMut.mutate(); }}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-white/80 hover:border-neon-teal/60 hover:text-white disabled:opacity-40"
          >
            {linksMut.isPending && <Spinner />} {t("social.handle.saveLinks")}
          </button>
        </div>
        {linksMsg && <p data-testid="links-msg" className={cn("font-mono text-[10px]", linksMut.isError ? "text-state-danger" : "text-neon-teal")}>{linksMsg}</p>}
      </div>
    </div>
  );
}
